import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Combine, Loader2, Plus, Search, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi, ewbInwardApi } from '@/services/ewayBillApi';
import { SupplierTable } from '@/components/ewayBill/SupplierTable';
import { SupplierDialog } from '@/components/ewayBill/SupplierDialog';
import { MergeSuppliersDialog } from '@/components/ewayBill/MergeSuppliersDialog';

/** Inward e-way bill senders: view, add by hand, fix (saved bills follow) and — Admin — merge duplicates. */
export default function SuppliersPage() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [denied, setDenied] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [edit, setEdit] = useState(null);
  const [merging, setMerging] = useState(false);
  useEffect(() => { ewbApi.config().then((c) => setStates(c.states || [])).catch(() => {}); }, []);
  const load = () => ewbInwardApi.suppliers(q).then(setData)
    .catch((e) => (e?.response?.status === 403 ? setDenied(errMsg(e, 'No access')) : toast.error(errMsg(e, 'Could not load the suppliers'))));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const rows = data?.suppliers || [];
  const pickedRows = rows.filter((s) => picked.has(s.id));
  const done = () => { setEdit(null); setMerging(false); setPicked(new Set()); load(); };
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="suppliers-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 h-16 flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" onClick={() => navigate('/eway-bills')} className="shrink-0 text-white hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-lg sm:text-xl font-bold whitespace-nowrap">Suppliers</h1>
          <span className="hidden md:inline text-xs text-white/60">Senders on inward e-way bills</span>
          {!denied && <Button onClick={() => setEdit({})} className="ml-auto px-3 sm:px-4 bg-amber-500 hover:bg-amber-600 text-[#1A1F2E]" data-testid="supplier-add-btn">
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Add supplier</span></Button>}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {denied ? (
          <div className="text-center py-16 text-gray-500 space-y-2" data-testid="suppliers-no-access"><ShieldAlert className="w-10 h-10 mx-auto text-amber-500" /><p>{denied}</p></div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, GSTIN, place or PIN" className="w-[300px] bg-white pl-9" data-testid="suppliers-search" />
              </div>
              {data && <span className="text-sm text-gray-500" data-testid="suppliers-count">{rows.length} supplier(s)</span>}
              {data?.can_merge && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-gray-500">{picked.size ? `${picked.size} ticked` : 'Tick duplicates to merge them'}</span>
                  <Button variant="outline" onClick={() => setMerging(true)} disabled={picked.size < 2} data-testid="suppliers-merge-btn"><Combine className="w-4 h-4 mr-1" />Merge</Button>
                </div>
              )}
            </div>
            {!data ? <Loader2 className="w-6 h-6 mx-auto my-10 animate-spin text-amber-600" />
              : rows.length === 0 ? <div className="rounded-xl bg-white py-14 text-center text-gray-500 shadow-sm" data-testid="suppliers-empty">No suppliers yet — they are saved when you save an inward bill, or add one by hand</div>
                : <SupplierTable rows={rows} canMerge={data.can_merge} picked={picked} onToggle={toggle} onEdit={setEdit} />}
          </>
        )}
      </main>
      <SupplierDialog supplier={edit} states={states} onClose={() => setEdit(null)} onSaved={done} />
      <MergeSuppliersDialog rows={pickedRows} open={merging} onClose={() => setMerging(false)} onMerged={done} />
    </div>
  );
}
